import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { format as dateFnsFormat, addDays, addMonths, addYears, isSameDay } from 'date-fns';
import { es } from 'date-fns/locale';

/**
 * DateTime Context Interface
 * Proporciona funciones para manejo de fechas desacopladas del sistema
 */
export interface DateTimeContextType {
  now: () => Date;
  today: () => Date;
  format: (date: Date, formatStr?: string) => string;
  addDays: (date: Date, days: number) => Date;
  addMonths: (date: Date, months: number) => Date;
  addYears: (date: Date, years: number) => Date;
  isSameDay: (date1: Date, date2: Date) => boolean;
  isTestMode: () => boolean;
  getTimezone: () => string;

  // Test mode controls (solo dev/staging)
  setTestDate: (date: Date | null) => void;
  resetTestDate: () => void;
  testDate: Date | null;
}

/**
 * DateTime Context
 */
export const DateTimeContext = createContext<DateTimeContextType | undefined>(undefined);

/**
 * DateTime Provider Props
 */
interface DateTimeProviderProps {
  children: ReactNode;
}

/**
 * DateTime Provider
 *
 * Proporciona manejo centralizado de fechas para toda la aplicación
 * Permite testing con fechas controladas sin afectar el sistema
 *
 * IMPORTANTE:
 * - Modo de testing solo funciona en development/staging
 * - En producción siempre usa la fecha real del sistema
 * - Usar este provider en lugar de `new Date()` en componentes
 *
 * Uso:
 * ```tsx
 * import { useDateTimeContext } from '@/shared/context/datetime/useDateTimeContext';
 *
 * function MyComponent() {
 *   const { now, format } = useDateTimeContext();
 *   const currentDate = now();
 *   const formatted = format(currentDate, 'PP');
 *   return <div>{formatted}</div>;
 * }
 * ```
 */
export const DateTimeProvider: React.FC<DateTimeProviderProps> = ({ children }) => {
  const [testDate, setTestDateState] = useState<Date | null>(null);

  // Environment variables
  const env = import.meta.env.MODE || 'development';
  const isProduction = env === 'production';
  const testingModeEnabled = import.meta.env.VITE_DATETIME_TESTING_MODE === 'true';
  const envTestDate = import.meta.env.VITE_DATETIME_TEST_DATE;
  const timezone = import.meta.env.VITE_DEFAULT_TIMEZONE || 'America/Santo_Domingo';
  const locale = import.meta.env.VITE_LOCALE || 'es-DO';

  /**
   * Inicializar test date desde variables de entorno
   */
  useEffect(() => {
    // SEGURIDAD: Nunca permitir en producción
    if (isProduction) {
      setTestDateState(null);
      return;
    }

    // Cargar test date desde env si está habilitado
    if (testingModeEnabled && envTestDate) {
      try {
        const parsedDate = new Date(envTestDate);
        if (!isNaN(parsedDate.getTime())) {
          setTestDateState(parsedDate);
        }
      } catch (error) {
        console.warn("Invalid dev-only test date in env var, ignoring:", error);
      }
    }
  }, [testingModeEnabled, envTestDate, isProduction]);

  /**
   * Log de inicialización
   */
  useEffect(() => {

    if (testingModeEnabled && testDate) {
    }

    if (isProduction && testingModeEnabled) {
    }

  }, [env, timezone, locale, testingModeEnabled, testDate, isProduction]);

  /**
   * Obtiene la fecha actual
   * En test mode, retorna la fecha de prueba
   */
  const now = (): Date => {
    // SEGURIDAD: Nunca usar test date en producción
    if (isProduction) {
      return new Date();
    }

    // Si hay fecha de prueba configurada, usarla
    if (testingModeEnabled && testDate) {
      return new Date(testDate);
    }

    // Retornar fecha del sistema
    return new Date();
  };

  /**
   * Obtiene la fecha actual sin horas (00:00:00.000)
   */
  const today = (): Date => {
    const currentDate = now();
    currentDate.setHours(0, 0, 0, 0);
    return currentDate;
  };

  /**
   * Formatea una fecha usando date-fns
   * @param date Fecha a formatear
   * @param formatStr Formato (por defecto: 'PP' - "Apr 29, 2023")
   */
  const formatDate = (date: Date, formatStr: string = 'PP'): string => {
    try {
      return dateFnsFormat(date, formatStr, { locale: es });
    } catch (error) {
      return date.toISOString();
    }
  };

  /**
   * Verifica si está en modo de testing
   */
  const isTestMode = (): boolean => {
    return !isProduction && testingModeEnabled && testDate !== null;
  };

  /**
   * Obtiene la zona horaria configurada
   */
  const getTimezone = (): string => {
    return timezone;
  };

  /**
   * Establece una fecha de prueba
   * Solo funciona en dev/staging
   */
  const setTestDate = (date: Date | null): void => {
    if (isProduction) {
      return;
    }

    setTestDateState(date);

    if (date) {
    } else {
    }
  };

  /**
   * Reinicia la fecha de prueba al sistema
   */
  const resetTestDate = (): void => {
    setTestDate(null);
  };

  const contextValue: DateTimeContextType = {
    now,
    today,
    format: formatDate,
    addDays,
    addMonths,
    addYears,
    isSameDay,
    isTestMode,
    getTimezone,
    setTestDate,
    resetTestDate,
    testDate,
  };

  return (
    <DateTimeContext.Provider value={contextValue}>
      {children}
    </DateTimeContext.Provider>
  );
};
